import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image ,
} from "@react-pdf/renderer";

// ✅ Styles
const styles = StyleSheet.create({
  page: {
    padding: 35,
    fontSize: 11,
    fontFamily: "Helvetica",
    lineHeight: 1.6,
    backgroundColor: "#FAFAFA",
  },
  header: {
    textAlign: "center",
    marginBottom: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0E8FE4",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: "#333",
    marginBottom: 8,
  },
  divider: {
    borderBottom: "1px solid #0E8FE4",
    marginVertical: 6,
  },
  infoBox: {
    border: "1px solid #C5C5C5",
    borderRadius: 5,
    padding: 10,
    marginBottom: 12,
    backgroundColor: "#FFFFFF",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  label: {
    fontWeight: "bold",
    color: "#0E3042",
  },
  sectionTitle: {
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 4,
    textDecoration: "underline",
    fontSize: 12,
  },
  paragraph: {
    marginBottom: 6,
    textAlign: "justify",
  },
  noteBox: {
    backgroundColor: "#E6F0FF",
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
  },
  consentAgreeRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 25,
    borderTop: "1px dashed #C5C5C5",
    paddingTop: 10,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  checkbox: {
    width: 12,
    height: 12,
    border: "1px solid #000",
    backgroundColor: "#0E8FE4", // checked box
    marginRight: 6,
  },
});

// ✅ Component
const ConsentPDF = ({ consentDetails ,signatureImage}) => {
  const details = consentDetails[0];
  const patient = details?.eve_id?.agg_sp_pt_id;
  const caller = details?.eve_id?.caller_id;

  const consentPoints = [
    "That, above named patient has been receiving treatment from a consultant for the mentioned disease.",
    "That, above named consultant has explained that the patient can now receive treatment/care at home.",
    "That, Spero Home Health Care professional has explained to us all the details, including limitations, advantages, disadvantages, effects, side-effects, pros, cons, etc., of Home Health Care.",
    `That, after understanding the limitations of Home Health Care in detail, I freely consent to the provision of Home Health Care, including healthcare attendants, for (${details?.eve_id?.sub_srv_id?.sub_srv_name || "__________"}) to ${patient?.name || "__________"} (Name of Patient).`,
    "That, we have been informed by a Spero professional that Spero Home Health Care is solely involved in executing medical instructions, advice, and orders from the respective consultant. Furthermore, we have been informed that we shall contact or consult the concerned consultant/hospital for any further medical management of the disease condition.",
    "That, we have been informed by a Spero professional that we shall adhere to all instructions, advice, and orders provided by the hospital through the concerned consultant. Additionally, we will consult or visit the relevant consultant/hospital for any necessary follow-up.",
    "That, we assure that we will not directly hire or avail Home Health Care services from any representative, professional, or employee of Spero Innovations Pvt. Ltd. in any form.",
    "We assure that the safety and security of the Spero Professional while rendering services at our home/premises will be our responsibility and liability.",
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ✅ Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Spero Home Health Care</Text>
          <Text style={styles.subtitle}>Spero Home Healthcare Pvt. Ltd.</Text>
          <Text>CONSENT FORM</Text>
          <View style={styles.divider} />
        </View>

        {/* ✅ Patient Info */}
        <View style={styles.infoBox}>
          <View style={styles.row}>
            <Text style={styles.label}>Patient Name:</Text>
            <Text>{patient?.name || "N/A"}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Event ID:</Text>
            <Text>{details?.eve_id?.eve_id || "N/A"}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Contact:</Text>
            <Text>{patient?.phone_no || "N/A"}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Email:</Text>
            <Text>{patient?.patient_email_id || "N/A"}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address:</Text>
            <Text style={{ width: "65%", textAlign: "right" }}>
              {patient?.address || "N/A"}
            </Text>
          </View>
        </View>

        {/* ✅ Consent Body */}
        <View>
          <Text style={styles.paragraph}>
            I, <Text style={styles.label}>{patient?.name || "__________"}</Text>, aged{" "}
            {patient?.Age || "____"} residing at {patient?.address || "__________"}, am
            suffering from ({patient?.Suffered_from || "__________"}).
          </Text>

          <Text style={styles.paragraph}>
            I, <Text style={styles.label}>{caller?.caller_fullname || "__________"}</Text>, aged{" "}
            {patient?.Age || "____"} residing at {patient?.address || "__________"}, am made aware that{" "}
            {patient?.name || "__________"} is suffering from (
            {patient?.Suffered_from || "__________"}).
          </Text>

          {/* ✅ List of Consent Points */}
          {consentPoints.map((point, index) => (
            <Text key={index} style={styles.paragraph}>
              {index + 1}. {point}
            </Text>
          ))}

          {/* ✅ Note Section */}
          <Text style={styles.sectionTitle}>Note:</Text>
          <View>
            <Text>
              1. Furthermore, we have been made to understand that Spero is a distinct and
              independent legal entity from the hospital. Spero will be solely responsible,
              liable, and answerable for Home Health Care services provided to the patient.
              The hospital will not be responsible, liable, or answerable for the policies,
              practices, and Home Health Care services provided by Spero, and any untoward
              event arising thereof. Therefore, in case of queries or feedback regarding
              Home Health Care services, etc., we would contact Spero.
            </Text>
          </View>

          <Text style={{ marginTop: 8 }}>
            2. We have been explained above information in our vernacular i.e. Marathi/Hindi
            and after satisfaction, we are putting our signatures at place and date
            mentioned here in below.
          </Text>
        </View>

        {/* ✅ Agree Section with Checked Checkbox and Signature */}
        <View style={styles.consentAgreeRow}>
          

          <View style={{ flexDirection: "column", alignItems: "center" }}>
    <Text>Signature:</Text>
    {signatureImage ? (
      <Image src={signatureImage} style={{ width: 120, height: 50, marginTop: 4 }} />
    ) : (
      <Text>__________________________</Text>
    )}
  </View>
</View>
      </Page>
    </Document>
  );
};

export default ConsentPDF;
